import { useSelector } from 'react-redux'
import * as R from 'ramda'
import { type Business, PermissionArea, Utils } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import {
  type BusinessSettingsSections,
  getSectionFields,
} from '~/store/duck/businessSettings'
import { getCRUDByArea, getCurrentBusinessRootId } from '~/store/reducers/auth'
import { getBusiness } from '~/store/reducers/businesses'
import {
  getFeatureToggle,
  getFeatureToggleForBusiness,
} from '~/store/reducers/constants'

import { PracticeDetailsPanels } from './practices'

export function usePracticeDetailsFields(businessId: string) {
  const alertConfigurationFields = useSelector(
    getSectionFields<BusinessSettingsSections['ALERT_CONFIGURATION']['fields']>(
      businessId,
      PracticeDetailsPanels.ALERT_CONFIGURATION,
    ),
  )
  const appointmentCommunicationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['APPOINTMENT_COMMUNICATIONS']['fields']
    >(businessId, PracticeDetailsPanels.APPOINTMENT_COMMUNICATIONS),
  )
  const appointmentConfigurationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['APPOINTMENT_CONFIGURATION']['fields']
    >(businessId, PracticeDetailsPanels.APPOINTMENT_CONFIGURATION),
  )
  const autoReplyCommunicationsFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['AUTO_REPLY_COMMUNICATIONS']['fields']
    >(businessId, PracticeDetailsPanels.AUTO_REPLY_COMMUNICATIONS),
  )
  const boopCommunicationsConfigFields = useSelector(
    getSectionFields<BusinessSettingsSections['BOOP_COMMUNICATIONS']['fields']>(
      businessId,
      PracticeDetailsPanels.BOOP_COMMUNICATIONS,
    ),
  )
  const brandingConfigurationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['BRANDING_CONFIGURATION']['fields']
    >(businessId, PracticeDetailsPanels.BRANDING_CONFIGURATION),
  )
  const chewyFields = useSelector(
    getSectionFields<BusinessSettingsSections['CHEWY']['fields']>(
      businessId,
      PracticeDetailsPanels.CHEWY,
    ),
  )
  const chewyGiftCardFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['CHEWY_GIFT_CARD_CONFIGURATION']['fields']
    >(businessId, PracticeDetailsPanels.CHEWY_GIFT_CARD_CONFIGURATION),
  )
  const countrySectionFields = useSelector(
    getSectionFields<BusinessSettingsSections['COUNTRY_SECTION']['fields']>(
      businessId,
      PracticeDetailsPanels.COUNTRY_SECTION,
    ),
  )
  const discountReasonConfigurationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['DISCOUNT_REASON_CONFIGURATION']['fields']
    >(businessId, PracticeDetailsPanels.DISCOUNT_REASON_CONFIGURATION),
  )
  const generalInformationFields = useSelector(
    getSectionFields<BusinessSettingsSections['GENERAL_INFORMATION']['fields']>(
      businessId,
      PracticeDetailsPanels.GENERAL_INFORMATION,
    ),
  )
  const groupDetailsConfigFields = useSelector(
    getSectionFields<BusinessSettingsSections['GROUP_DETAILS']['fields']>(
      businessId,
      PracticeDetailsPanels.GROUP_DETAILS,
    ),
  )
  const invoiceConfigurationsFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['INVOICE_ESTIMATE_CONFIGURATION']['fields']
    >(businessId, PracticeDetailsPanels.INVOICE_ESTIMATE_CONFIGURATION),
  )
  const kioskConfigurationsFields = useSelector(
    getSectionFields<BusinessSettingsSections['KIOSK']['fields']>(
      businessId,
      PracticeDetailsPanels.KIOSK,
    ),
  )
  const labelConfigurationFields = useSelector(
    getSectionFields<BusinessSettingsSections['LABEL_CONFIGURATION']['fields']>(
      businessId,
      PracticeDetailsPanels.LABEL_CONFIGURATION,
    ),
  )
  const licensesFields = useSelector(
    getSectionFields<BusinessSettingsSections['LICENSES']['fields']>(
      businessId,
      PracticeDetailsPanels.LICENSES,
    ),
  )
  const localizationFields = useSelector(
    getSectionFields<BusinessSettingsSections['LOCALIZATIONS']['fields']>(
      businessId,
      PracticeDetailsPanels.LOCALIZATIONS,
    ),
  )
  const mainFields = useSelector(
    getSectionFields<BusinessSettingsSections['MAIN']['fields']>(
      businessId,
      PracticeDetailsPanels.MAIN,
    ),
  )
  const mobileFields = useSelector(
    getSectionFields<BusinessSettingsSections['MOBILE']['fields']>(
      businessId,
      PracticeDetailsPanels.MOBILE,
    ),
  )
  const npsSystemFields = useSelector(
    getSectionFields<BusinessSettingsSections['NPS_SYSTEM']['fields']>(
      businessId,
      PracticeDetailsPanels.NPS_SYSTEM,
    ),
  )
  const marketplaceManagementFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['MARKETPLACE_MANAGEMENT']['fields']
    >(businessId, PracticeDetailsPanels.MARKETPLACE_MANAGEMENT),
  )
  const savedSignatureConfigurationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['SAVED_SIGNATURE_CONFIGURATION']['fields']
    >(businessId, PracticeDetailsPanels.SAVED_SIGNATURE_CONFIGURATION),
  )
  const sharingFields = useSelector(
    getSectionFields<BusinessSettingsSections['SHARING']['fields']>(
      businessId,
      PracticeDetailsPanels.SHARING,
    ),
  )
  const soapConfigurationFields = useSelector(
    getSectionFields<BusinessSettingsSections['SOAP_CONFIGURATION']['fields']>(
      businessId,
      PracticeDetailsPanels.SOAP_CONFIGURATION,
    ),
  )
  const socialMediaFields = useSelector(
    getSectionFields<BusinessSettingsSections['SOCIAL_MEDIA']['fields']>(
      businessId,
      PracticeDetailsPanels.SOCIAL_MEDIA,
    ),
  )
  const recordSharingFields = useSelector(
    getSectionFields<BusinessSettingsSections['RECORD_SHARING']['fields']>(
      businessId,
      PracticeDetailsPanels.RECORD_SHARING,
    ),
  )
  const remindersSettingsFields = useSelector(
    getSectionFields<BusinessSettingsSections['REMINDER_SET_UP']['fields']>(
      businessId,
      PracticeDetailsPanels.REMINDER_SET_UP,
    ),
  )
  const taxConfigurationFields = useSelector(
    getSectionFields<BusinessSettingsSections['TAX_CONFIGURATION']['fields']>(
      businessId,
      PracticeDetailsPanels.TAX_CONFIGURATION,
    ),
  )
  const telehealthIntegrationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['TELEHEALTH_INTEGRATION']['fields']
    >(businessId, PracticeDetailsPanels.TELEHEALTH_INTEGRATION),
  )
  const vaccineCertificateFooterFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['VACCINE_CERTIFICATE_FOOTERS']['fields']
    >(businessId, PracticeDetailsPanels.VACCINE_CERTIFICATE_FOOTERS),
  )
  const unitsOfMeasurementFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['UNITS_OF_MEASUREMENT']['fields']
    >(businessId, PracticeDetailsPanels.UNITS_OF_MEASUREMENT),
  )
  const wellnessPlansFields = useSelector(
    getSectionFields<BusinessSettingsSections['WELLNESS_PLANS']['fields']>(
      businessId,
      PracticeDetailsPanels.WELLNESS_PLANS,
    ),
  )

  return {
    alertConfigurationFields,
    appointmentCommunicationFields,
    appointmentConfigurationFields: R.omit(
      ['occupyAllStaffTimeslots'],
      appointmentConfigurationFields,
    ),
    autoReplyCommunicationsFields,
    boopCommunicationsConfigFields,
    brandingConfigurationFields,
    chewyFields,
    chewyGiftCardFields,
    countrySectionFields,
    discountReasonConfigurationFields,
    generalInformationFields,
    groupDetailsConfigFields,
    invoiceConfigurationsFields,
    kioskConfigurationsFields,
    labelConfigurationFields,
    licensesFields,
    localizationFields,
    mainFields,
    mobileFields,
    npsSystemFields,
    marketplaceManagementFields,
    savedSignatureConfigurationFields,
    sharingFields,
    soapConfigurationFields,
    socialMediaFields,
    recordSharingFields,
    remindersSettingsFields,
    taxConfigurationFields,
    telehealthIntegrationFields,
    vaccineCertificateFooterFields,
    unitsOfMeasurementFields,
    wellnessPlansFields,
  }
}

const shouldShowBoopCommunications = (
  hideBoopConfig: boolean,
  isAutoReplyEnabled: boolean,
  isGroup: boolean,
) => !hideBoopConfig && !isAutoReplyEnabled && !isGroup

const shouldShowChewyGiftCardConfig = (
  isCvcAutomatedGiftCardEnabled: boolean,
  hasKyriosPermission: boolean,
  isGroup: boolean,
  omniChannel: boolean | undefined,
) =>
  isCvcAutomatedGiftCardEnabled &&
  hasKyriosPermission &&
  !isGroup &&
  Boolean(omniChannel)

const shouldShowLabIntegrations = (isGroup: boolean) =>
  (!Utils.isPlayEnvironment() || !Utils.isEuropeRegion()) && !isGroup

const shouldShowRecordSharing = (
  isGroup: boolean,
  isGroupClientSharingEnabled: boolean,
  isPatientSharingEnabled: boolean,
) => isGroup && isGroupClientSharingEnabled && !isPatientSharingEnabled

const shouldShowMobile = (
  hideBoopConfig: boolean,
  hasSupportPermission: boolean,
  isGroup: boolean,
) => !hideBoopConfig && hasSupportPermission && !isGroup

export const useGetPracticeSectionRenderConditions = ({
  hasTaxes,
  hideBoopConfig,
  isGroup,
  practiceId,
}: {
  hasTaxes: boolean
  hideBoopConfig: boolean
  isGroup: boolean
  practiceId: string
}) => {
  const businessRootId = useSelector(getCurrentBusinessRootId)

  // Permissions
  const migrationPermissions = useSelector(getCRUDByArea(PermissionArea.IMPORT))
  const ssoSettingsPermissions = useSelector(
    getCRUDByArea(PermissionArea.SSO_SETTINGS),
  )
  const reminderProtocolsPermissions = useSelector(
    getCRUDByArea(PermissionArea.REMINDER_PROTOCOLS),
  )
  const kyriosClinicIdPermissions = useSelector(
    getCRUDByArea(PermissionArea.KYRIOS_CLINIC_ID),
  )
  const supportPermissions = useSelector(getCRUDByArea(PermissionArea.SUPPORT))

  const { omniChannel } =
    useSelector(getBusiness(practiceId)) || ({} as Business)

  // Feature Toggles
  const isCvcAutomatedGiftCardEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_AUTOMATED_GIFT_CARDS),
  )

  const isVetcoveSetupEnabled = useSelector(
    getFeatureToggle(FeatureToggle.VETCOVE_SETUP),
  )
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const isGroupClientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.GROUP_CLIENT_SHARING),
  )
  const isTeamAssignmentEnabled = useSelector(
    getFeatureToggle(FeatureToggle.TEAM_ASSIGNMENT),
  )
  const isAutoRolesAssignEnabled = useSelector(
    getFeatureToggleForBusiness(
      FeatureToggle.AUTO_CWAV_VCR_ROLES_ASSIGN,
      practiceId,
    ),
  )
  const isAutoReplyRhapsodyCommunicationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AUTO_REPLY_RHAPSODY_COMMUNICATION),
  )
  const isWellnessplanSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.WELLNESSPLAN_SHARING),
  )
  const isDiscountReasonEnabled = useSelector(
    getFeatureToggleForBusiness(FeatureToggle.DISCOUNT_REASON, practiceId),
  )

  const showAutoReplyRhapsodyCommunication =
    isAutoReplyRhapsodyCommunicationEnabled && !isGroup
  const showBoopCommunications = shouldShowBoopCommunications(
    hideBoopConfig,
    isAutoReplyRhapsodyCommunicationEnabled,
    isGroup,
  )
  const showChewy = kyriosClinicIdPermissions.read && !isGroup
  const showChewyGiftCardConfig = shouldShowChewyGiftCardConfig(
    isCvcAutomatedGiftCardEnabled,
    kyriosClinicIdPermissions.read,
    isGroup,
    omniChannel,
  )
  const showCountrySection = supportPermissions.read && !isGroup
  const showGroupAccessRoles = isGroup && isAutoRolesAssignEnabled
  const showLabIntegrations = shouldShowLabIntegrations(isGroup)
  const showMarketplaceManagement = supportPermissions.read && !isGroup
  const showMigration = migrationPermissions.read && !isGroup
  const showMobile = shouldShowMobile(
    hideBoopConfig,
    supportPermissions.read,
    isGroup,
  )
  const showNpsSystem = !isGroup && practiceId !== businessRootId
  const showRecordSharing = shouldShowRecordSharing(
    isGroup,
    isGroupClientSharingEnabled,
    isPatientSharingEnabled,
  )
  const showReminderSetup = reminderProtocolsPermissions.read && !isGroup
  const showSharing = isGroup && isWellnessplanSharingEnabled
  const showSSOConfig = !isGroup && ssoSettingsPermissions.update
  const showTaxConfig = !isGroup && hasTaxes
  const showTeamConfig = !isGroup && isTeamAssignmentEnabled
  const showVetCoveIntegration = !isGroup && isVetcoveSetupEnabled

  return {
    [PracticeDetailsPanels.MAIN]: true,
    [PracticeDetailsPanels.GENERAL_INFORMATION]: true,
    [PracticeDetailsPanels.ALERT_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.APPOINTMENT_COMMUNICATIONS]: !isGroup,
    [PracticeDetailsPanels.AUTO_REPLY_COMMUNICATIONS]:
      showAutoReplyRhapsodyCommunication,
    [PracticeDetailsPanels.APPOINTMENT_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.BOOP_COMMUNICATIONS]: showBoopCommunications,
    [PracticeDetailsPanels.BRANDING_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.CHEWY]: showChewy,
    [PracticeDetailsPanels.CHEWY_GIFT_CARD_CONFIGURATION]:
      showChewyGiftCardConfig,
    [PracticeDetailsPanels.COUNTRY_SECTION]: showCountrySection,
    [PracticeDetailsPanels.DEPARTMENT]: !isGroup,
    [PracticeDetailsPanels.DISCOUNT_REASON_CONFIGURATION]:
      isDiscountReasonEnabled,
    [PracticeDetailsPanels.GROUP_ACCESS_ROLES]: showGroupAccessRoles,
    [PracticeDetailsPanels.GROUP_DETAILS]: isGroup,
    [PracticeDetailsPanels.IMAGING_INTEGRATIONS]: !isGroup,
    [PracticeDetailsPanels.INVOICE_ESTIMATE_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.KIOSK]: !isGroup,
    [PracticeDetailsPanels.LAB_INTEGRATIONS]: showLabIntegrations,
    [PracticeDetailsPanels.LABEL_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.LICENSES]: !isGroup,
    [PracticeDetailsPanels.LOCALIZATIONS]: !isGroup,
    [PracticeDetailsPanels.MARKETPLACE_MANAGEMENT]: showMarketplaceManagement,
    [PracticeDetailsPanels.MIGRATION_V2]: showMigration,
    [PracticeDetailsPanels.MOBILE]: showMobile,
    [PracticeDetailsPanels.NPS_SYSTEM]: showNpsSystem,
    [PracticeDetailsPanels.RECORD_SHARING]: showRecordSharing,
    [PracticeDetailsPanels.REMINDER_SET_UP]: showReminderSetup,
    [PracticeDetailsPanels.RHAPSODY_PAY]: !isGroup,
    [PracticeDetailsPanels.SAVED_SIGNATURE_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.SHARING]: showSharing,
    [PracticeDetailsPanels.SOAP_CONFIGURATION]: !isGroup,
    [PracticeDetailsPanels.SOCIAL_MEDIA]: !isGroup,
    [PracticeDetailsPanels.SSO_CONFIGURATION]: showSSOConfig,
    [PracticeDetailsPanels.TAX_CONFIGURATION]: showTaxConfig,
    [PracticeDetailsPanels.TEAM_CONFIGURATION]: showTeamConfig,
    [PracticeDetailsPanels.TELEHEALTH_INTEGRATION]: !isGroup,
    [PracticeDetailsPanels.UNITS_OF_MEASUREMENT]: !isGroup,
    [PracticeDetailsPanels.VACCINE_CERTIFICATE_FOOTERS]: !isGroup,
    [PracticeDetailsPanels.VETCOVE_INTEGRATION]: showVetCoveIntegration,
    [PracticeDetailsPanels.WELLNESS_PLANS]: !isGroup,
  } as Record<PracticeDetailsPanels, boolean>
}
